import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { TextInput } from '@/UIComponents2/TextInput'
import { IconCircle } from '@/components/CircleIcon'
import { cn } from '@/utlis/cn'
import { other } from './constants'

type Entry = {
    value: string
    label: string
    icon?: React.ElementType
}

type Props = {
    title: string
    itemType?: 'bubble' | 'default'
    entries: Entry[]
    selectedEntries: string[]
    setSelectedEntries: (entries: string[]) => void
    otherEntrySelected: boolean
    setOtherEntrySelected: (selected: boolean) => void
    otherEntry: string
    setOtherEntry: (entry: string) => void
}

export const OnboardingFormSection: FC<Props> = ({
    title,
    itemType = 'default',
    entries,
    selectedEntries,
    setSelectedEntries,
    otherEntrySelected,
    setOtherEntrySelected,
    otherEntry,
    setOtherEntry,
}) => {
    const { t } = useTranslation()
    const formWidth = 'max-w-[800px] min-w-[515px]'

    const content = useMemo(() => {
        return entries.map((entry) => (
            <EntryCheckbox
                key={entry.value}
                entry={entry}
                checked={selectedEntries.includes(entry.value)}
                onToggle={() => {
                    if (selectedEntries.includes(entry.value)) {
                        setSelectedEntries(selectedEntries.filter((i) => i !== entry.value))
                    } else {
                        setSelectedEntries([...selectedEntries, entry.value])
                    }
                }}
                itemType={itemType}
            />
        ))
    }, [entries, selectedEntries])

    return (
        <fieldset className={cn('flex flex-col gap-[32px]', formWidth)}>
            <div className="flex flex-col gap-[8px]">
                <span className="text-nowrap w-full t-title bg-linear-[98deg,white_21.88%,transparent_139.38%] bg-clip-text text-transparent">
                    {title}
                </span>
                <span className="w-full text-[15px] font-[300] text-[rgba(255,255,255,0.75)]">
                    {t('You can choose several options')}
                </span>
            </div>
            <ul className={'flex flex-wrap gap-[12px]'}>
                {content}
                <EntryCheckbox
                    key={'other'}
                    entry={other}
                    checked={otherEntrySelected}
                    onToggle={() => {
                        setOtherEntrySelected(!otherEntrySelected)
                    }}
                    itemType={itemType}
                />
            </ul>
            {otherEntrySelected && (
                <TextInput
                    className="max-w-[500px]"
                    value={otherEntry}
                    onChange={(value) => setOtherEntry(value)}
                    placeholder={title}
                />
            )}
        </fieldset>
    )
}

type EntryCheckboxProps = {
    entry: Entry
    checked: boolean
    onToggle: () => void
    itemType: 'bubble' | 'default'
}

function EntryCheckbox(props: EntryCheckboxProps) {
    let listItemStyle =
        'w-[250px] h-[40px] rounded-[12px] bg-[rgba(255,255,255,0.1)] flex items-center px-[16px] cursor-pointer'
    let borderStyle =
        'border border-white/10 hover:border-white/20 shadow-[inset_2px_4px_16px_0px_rgba(255,255,255,0.06)]'
    let checkboxStyle = ''

    if (props.itemType === 'bubble') {
        listItemStyle =
            'w-[237px] h-[160px] rounded-[18px] bg-[rgba(255,255,255,0.1)] flex flex-col pl-[16px] pr-[4px] pt-[4px] cursor-pointer'
        checkboxStyle = 'rounded-[8px] h-[26px] w-[26px] flex items-center justify-center'
        const bubbleBg = 'bg-[url("/images/onboarding-form-sparkle.webp")] bg-no-repeat bg-top-left'
        listItemStyle = cn(listItemStyle, bubbleBg)
    }

    const selectedStyle =
        'border border-[rgba(252,230,64,0.3)] shadow-[inset_0px_0px_16px_0px_rgba(252,230,64,0.24)] outline-1 outline-[rgba(252,230,64,0.3)]'

    return (
        <li
            key={props.entry.value}
            className={cn(listItemStyle, props.checked ? selectedStyle : borderStyle)}
            onClick={props.onToggle}
        >
            {props.itemType === 'bubble' ? (
                <>
                    <div className="flex items-start justify-between w-full h-[50%] pr-[8px] pt-[8px]">
                        <IconCircle className="size-[50px]">
                            {props.entry.icon && <props.entry.icon style={{ fontSize: '1.4rem' }} />}
                        </IconCircle>
                        <div onClick={(e) => e.stopPropagation()}>
                            <Checkbox className={cn(checkboxStyle)} checked={props.checked} onChange={props.onToggle} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between w-full h-[50%] pl-[16px]">
                        <span className="text-[16px] font-[300]">{props.entry.label}</span>
                    </div>
                </>
            ) : (
                <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        className={checkboxStyle}
                        checked={props.checked}
                        onChange={props.onToggle}
                        label={props.entry.label}
                    />
                </div>
            )}
        </li>
    )
}
